import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router";
import { useDraggable } from "react-use-draggable-scroll";

import Datepicker from "src/app/components/datepicker/Datepicker";
import { Icon } from "src/app/components/icon/Icon";
import statisticsCSS from "src/app/modules/admin/components/statistics/Statistics.module.css";
import Brands from "src/app/modules/admin/components/statistics/components/Brands";
import Partners from "src/app/modules/admin/components/statistics/components/Partners";
import RegionPartners from "src/app/modules/admin/components/statistics/components/RegionPartners";
import RegionSystems from "src/app/modules/admin/components/statistics/components/RegionSystems";
import Systems from "src/app/modules/admin/components/statistics/components/Systems";
import TargetGroupProjects from "src/app/modules/admin/components/statistics/components/TargetGroupProjects";
import TargetGroupSystems from "src/app/modules/admin/components/statistics/components/TargetGroupSystems";
import TargetGroupUsers from "src/app/modules/admin/components/statistics/components/TargetGroupUsers";
import { STATISTICS_TABS } from "src/app/modules/admin/components/statistics/constants";

function Projects() {
  const QUERY_PARAMS: {
    TAB: string;
    DATE_FROM: string;
    DATE_TO: string;
  } = Object.freeze({
    TAB: "tab",
    DATE_FROM: "dateFrom",
    DATE_TO: "dateTo",
  });

  const [activeTab, setActiveTab] = useState(getQueryParam(QUERY_PARAMS.TAB) ?? STATISTICS_TABS.PARTNERS);

  const [filteredDateFrom, setFilteredDateFrom] = useState<Date | undefined>(
    getQueryParam(QUERY_PARAMS.DATE_FROM) ? new Date(getQueryParam(QUERY_PARAMS.DATE_FROM) ?? "") : undefined
  );
  const [filteredDateTo, setFilteredDateTo] = useState<Date | undefined>(
    getQueryParam(QUERY_PARAMS.DATE_TO) ? new Date(getQueryParam(QUERY_PARAMS.DATE_TO) ?? "") : undefined
  );

  const ref = useRef<HTMLDivElement>() as React.MutableRefObject<HTMLInputElement>;
  const { events } = useDraggable(ref);

  const [searchParams, setSearchParams] = useState<{ tab: string; dateFrom: string; dateTo: string } | null>(null);

  const history = useHistory();

  function getQueryParam(key: string): string | null {
    const params = new URLSearchParams(window.location.search.substring(1));
    const value = params.get(key);
    if (value && value !== "") {
      return value;
    }

    return null;
  }

  useEffect(() => {
    if (searchParams) {
      const url = new URL(window.location.href);
      url.searchParams.set(QUERY_PARAMS.TAB, searchParams.tab ?? "");
      searchParams.dateFrom ? url.searchParams.set(QUERY_PARAMS.DATE_FROM, searchParams.dateFrom) : url.searchParams.delete(QUERY_PARAMS.DATE_FROM);
      searchParams.dateFrom ? url.searchParams.set(QUERY_PARAMS.DATE_TO, searchParams.dateTo) : url.searchParams.delete(QUERY_PARAMS.DATE_TO);
      history.replace(url.search);
    }
  }, [QUERY_PARAMS.DATE_FROM, QUERY_PARAMS.DATE_TO, QUERY_PARAMS.TAB, history, searchParams]);

  const onTabChange = (tab: string) => {
    setActiveTab(tab);
    setSearchParams({
      tab: tab,
      dateFrom: filteredDateFrom?.toDateString() ?? "",
      dateTo: filteredDateTo?.toDateString() ?? "",
    });
  };

  const onDateChange = (dates: any) => {
    const [start, end] = dates;
    setFilteredDateFrom(start);
    setFilteredDateTo(end);
    setSearchParams({
      tab: activeTab,
      dateFrom: start?.toDateString() ?? "",
      dateTo: end?.toDateString() ?? "",
    });
  };

  const removeDateFilter = () => {
    setFilteredDateFrom(undefined);
    setFilteredDateTo(undefined);
    setSearchParams({
      tab: activeTab,
      dateFrom: "",
      dateTo: "",
    });
  };

  return (
    <div className="u-bg-background">
      <div className={classNames("u-box", "u-flex-wrap", "u-t-align-center", "u-pos-relative", statisticsCSS.statisticsContainer)}>
        <div>
          <h1 className="u-t-primary u-m0">
            <span className="u-t-uppercase">Statistika</span>
          </h1>
        </div>
        <div className={classNames("u-flex", "u-flex-column", "u-col-lg-7", "u-col-md-8", "u-col-sm-7", "u-t-align-start", "u-mb30px", statisticsCSS.inputs)}>
          <label className="u-t-f14p u-ml10px u-t-w400 u-t-lh20" htmlFor="partner">
            Filtriraj po vremenu
          </label>
          <div className={classNames("u-flex", "u-flex-row", "u-flex-gap-20", statisticsCSS.datepickerWrap)}>
            <div className="u-flex u-flex-column u-flex-gap-20 u-col-lg-6 u-col-md-7 u-col-sm-7 u-col-xs-7 u-flex-items-center">
              <Datepicker startDate={filteredDateFrom} endDate={filteredDateTo} handleChange={onDateChange} />
            </div>
            {(filteredDateFrom || filteredDateTo) && (
              <button
                onClick={() => removeDateFilter()}
                className={classNames(
                  "u-flex",
                  "u-flex-row",
                  "u-pt10px",
                  "u-flex-items-center",
                  "u-flex-gap-10",
                  "u-hfit",
                  "u-col-lg-4",
                  "u-col-md-4",
                  "u-col-sm-5",
                  "u-cursor-pointer"
                )}
              >
                <h6 className="u-m0 u-t-primary u-t-w400">ukloni filter</h6>
                <Icon name="trash" className="u-t-primary" />
              </button>
            )}
          </div>
        </div>
        <div className={classNames("u-flex", "u-col-lg-12", "u-border-box", "u-flex-content-between", "u-flex-wrap", statisticsCSS.chartWrap)}>
          <div className={classNames(statisticsCSS.tabsContainer)} {...events} ref={ref}>
            <button
              className={classNames(
                activeTab === STATISTICS_TABS.PARTNERS ? "active u-bg-white active-tab-shadow" : "",
                "u-h100",
                "u-flex",
                "u-flex-content-center",
                "u-flex-items-center",
                "u-cursor-pointer"
              )}
              onClick={() => onTabChange(STATISTICS_TABS.PARTNERS)}
            >
              <h6 className={classNames(activeTab === STATISTICS_TABS.PARTNERS ? "active u-t-primary" : "u-t-secondary", "u-m0")}>Partneri</h6>
            </button>
            <button
              className={classNames(
                activeTab === STATISTICS_TABS.SYSTEMS ? "active u-bg-white active-tab-shadow" : "",
                "u-h100",
                "u-flex",
                "u-flex-content-center",
                "u-flex-items-center",
                "u-cursor-pointer"
              )}
              onClick={() => onTabChange(STATISTICS_TABS.SYSTEMS)}
            >
              <h6 className={classNames(activeTab === STATISTICS_TABS.SYSTEMS ? "active u-t-primary" : "u-t-secondary", "u-m0")}>Sustavi</h6>
            </button>
            <button
              className={classNames(
                activeTab === STATISTICS_TABS.BRANDS ? "active u-bg-white active-tab-shadow" : "",
                "u-h100",
                "u-flex",
                "u-flex-content-center",
                "u-flex-items-center",
                "u-cursor-pointer"
              )}
              onClick={() => onTabChange(STATISTICS_TABS.BRANDS)}
            >
              <h6 className={classNames(activeTab === STATISTICS_TABS.BRANDS ? "active u-t-primary" : "u-t-secondary", "u-m0")}>Brandovi</h6>
            </button>
            <button
              className={classNames(
                activeTab === STATISTICS_TABS.REGION_PARTNERS ? "active u-bg-white active-tab-shadow" : "",
                "u-h100",
                "u-flex",
                "u-flex-content-center",
                "u-flex-items-center",
                "u-cursor-pointer"
              )}
              onClick={() => onTabChange(STATISTICS_TABS.REGION_PARTNERS)}
            >
              <h6 className={classNames(activeTab === STATISTICS_TABS.REGION_PARTNERS ? "active u-t-primary" : "u-t-secondary", "u-m0")}>Regije - Partneri</h6>
            </button>
            <button
              className={classNames(
                activeTab === STATISTICS_TABS.REGION_SYSTEMS ? "active u-bg-white active-tab-shadow" : "",
                "u-h100",
                "u-flex",
                "u-flex-content-center",
                "u-flex-items-center",
                "u-cursor-pointer"
              )}
              onClick={() => onTabChange(STATISTICS_TABS.REGION_SYSTEMS)}
            >
              <h6 className={classNames(activeTab === STATISTICS_TABS.REGION_SYSTEMS ? "active u-t-primary" : "u-t-secondary", "u-m0")}>Sustavi po regijama</h6>
            </button>
            <button
              className={classNames(
                activeTab === STATISTICS_TABS.TARGET_GROUP_PROJECTS ? "active u-bg-white active-tab-shadow" : "",
                "u-h100",
                "u-flex",
                "u-flex-content-center",
                "u-flex-items-center",
                "u-cursor-pointer"
              )}
              onClick={() => onTabChange(STATISTICS_TABS.TARGET_GROUP_PROJECTS)}
            >
              <h6 className={classNames(activeTab === STATISTICS_TABS.TARGET_GROUP_PROJECTS ? "active u-t-primary" : "u-t-secondary", "u-m0")}>
                Upiti po ciljnim skupinama
              </h6>
            </button>
            <button
              className={classNames(
                activeTab === STATISTICS_TABS.TARGET_GROUP_USERS ? "active u-bg-white active-tab-shadow" : "",
                "u-h100",
                "u-flex",
                "u-flex-content-center",
                "u-flex-items-center",
                "u-cursor-pointer"
              )}
              onClick={() => onTabChange(STATISTICS_TABS.TARGET_GROUP_USERS)}
            >
              <h6 className={classNames(activeTab === STATISTICS_TABS.TARGET_GROUP_USERS ? "active u-t-primary" : "u-t-secondary", "u-m0")}>
                Registrirani po ciljnim skupinama
              </h6>
            </button>
            <button
              className={classNames(
                activeTab === STATISTICS_TABS.TARGET_GROUP_SYSTEMS ? "active u-bg-white active-tab-shadow" : "",
                "u-h100",
                "u-flex",
                "u-flex-content-center",
                "u-flex-items-center",
                "u-cursor-pointer"
              )}
              onClick={() => onTabChange(STATISTICS_TABS.TARGET_GROUP_SYSTEMS)}
            >
              <h6 className={classNames(activeTab === STATISTICS_TABS.TARGET_GROUP_SYSTEMS ? "active u-t-primary" : "u-t-secondary", "u-m0")}>
                Sustavi po ciljnim skupinama
              </h6>
            </button>
          </div>

          <Partners activeTab={activeTab} filteredDateFrom={filteredDateFrom} filteredDateTo={filteredDateTo} />
          <Systems activeTab={activeTab} filteredDateFrom={filteredDateFrom} filteredDateTo={filteredDateTo} />
          <Brands activeTab={activeTab} filteredDateFrom={filteredDateFrom} filteredDateTo={filteredDateTo} />
          <RegionPartners activeTab={activeTab} filteredDateFrom={filteredDateFrom} filteredDateTo={filteredDateTo} />
          <RegionSystems activeTab={activeTab} filteredDateFrom={filteredDateFrom} filteredDateTo={filteredDateTo} />
          <TargetGroupProjects activeTab={activeTab} filteredDateFrom={filteredDateFrom} filteredDateTo={filteredDateTo} />
          <TargetGroupUsers activeTab={activeTab} filteredDateFrom={filteredDateFrom} filteredDateTo={filteredDateTo} />
          <TargetGroupSystems activeTab={activeTab} filteredDateFrom={filteredDateFrom} filteredDateTo={filteredDateTo} />
        </div>
      </div>
    </div>
  );
}

export default Projects;
